import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import grids from "../responsive-fixes/grids.module.css";

type Tab = "generator" | "templates" | "history" | "usage";
type ContentType = "social_post" | "product_description" | "whatsapp_message" | "blog_post";

interface Template {
  id: string;
  label: string;
  description: string;
  type: ContentType;
  preset: {
    product?: string;
    platform?: string;
    language?: string;
    features?: string;
    offer?: string;
    audience?: string;
    keyword?: string;
    length?: number;
  };
}

const TEMPLATES: Template[] = [
  {
    id: "ig-enzo-dryer",
    label: "Instagram — ENZO Salon Dryer",
    description: "IG caption for the ENZO EN-6622 wall-mounted salon dryer.",
    type: "social_post",
    preset: { product: "ENZO EN-6622 3000W Wall-Mounted Salon Hair Dryer", platform: "instagram", language: "en" },
  },
  {
    id: "tiktok-lizze",
    label: "TikTok — LIZZE Extreme",
    description: "Punchy TikTok hook for the LIZZE EXTREME straightener.",
    type: "social_post",
    preset: { product: "Lizze EXTREME Professional Hair Straightener", platform: "tiktok", language: "en" },
  },
  {
    id: "fb-barber-clipper",
    label: "Facebook — ENZO Barber Clipper",
    description: "Facebook post for the ENZO EN-1920 cordless barber clipper.",
    type: "social_post",
    preset: { product: "ENZO EN-1920 Cordless Barber Clippers LCD", platform: "facebook", language: "en" },
  },
  {
    id: "pd-enzo-flat-iron",
    label: "Product Desc — ENZO Flat Iron",
    description: "Shopify product copy for the ENZO EN-3999 steam flat iron.",
    type: "product_description",
    preset: {
      product: "ENZO EN-3999 Ionic Steam Flat Iron",
      features: "Ceramic titanium plates\nIonic steam technology\nFrizz control for daily use\nMCH heating element\nAuto shut-off safety",
    },
  },
  {
    id: "pd-lizze-dryer",
    label: "Product Desc — LIZZE Pro Dryer",
    description: "Shopify copy for the LIZZE Professional Hair Dryer.",
    type: "product_description",
    preset: {
      product: "LIZZE Professional Hair Dryer",
      features: "Salon-grade motor\nNegative ion output\nMultiple heat and speed settings\nVerified authentic",
    },
  },
  {
    id: "wa-salon-wholesale",
    label: "WhatsApp — Salon Wholesale Offer",
    description: "Broadcast to salon wholesale contacts with a featured offer.",
    type: "whatsapp_message",
    preset: { offer: "20% off all ENZO professional tools this week — wholesale pricing for salons", audience: "salon" },
  },
  {
    id: "wa-retail-new",
    label: "WhatsApp — New Retail Customer",
    description: "Warm welcome broadcast with intro offer for retail customers.",
    type: "whatsapp_message",
    preset: { offer: "Welcome! Get 10% off your first order of LIZZE or ENZO products. Free delivery across UAE.", audience: "retail" },
  },
  {
    id: "blog-keratin",
    label: "Blog — Best Keratin Treatment Dubai",
    description: "SEO post targeting 'best keratin treatment dubai'.",
    type: "blog_post",
    preset: { keyword: "best keratin treatment dubai", length: 600 },
  },
  {
    id: "blog-barber-clippers",
    label: "Blog — Pro Barber Clippers UAE",
    description: "SEO post for 'professional barber clippers uae'.",
    type: "blog_post",
    preset: { keyword: "professional barber clippers uae", length: 600 },
  },
];

interface HistoryItem {
  id: number;
  type: string;
  request: any;
  output: any;
  created_at: string;
}

interface UsageStats {
  total_generations: number;
  by_type: Record<string, number>;
  estimated_cost: number;
}

const TYPE_LABELS: Record<ContentType, string> = {
  social_post: "Social Post",
  product_description: "Product Description",
  whatsapp_message: "WhatsApp Broadcast",
  blog_post: "Blog Post",
};

const glassCard: React.CSSProperties = {
  background: "rgba(255,255,255,0.02)",
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: "0.5px solid rgba(189,178,255,0.15)",
  borderRadius: 16,
  padding: 24,
  color: "#dde5ff",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  background: "rgba(6,14,32,0.6)",
  border: "0.5px solid rgba(189,178,255,0.2)",
  borderRadius: 12,
  color: "#dde5ff",
  fontFamily: "inherit",
  fontSize: 14,
  outline: "none",
};

const buttonStyle: React.CSSProperties = {
  padding: "12px 20px",
  background: "linear-gradient(135deg, #bdb2ff, #cfeda2)",
  color: "#060e20",
  border: "none",
  borderRadius: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 14,
};

const ghostButton: React.CSSProperties = {
  ...buttonStyle,
  background: "transparent",
  color: "#bdb2ff",
  border: "0.5px solid rgba(189,178,255,0.3)",
};

function renderOutput(item: HistoryItem): string {
  const o = item.output;
  if (typeof o === "string") return o;
  if (o && typeof o === "object") {
    if (o.content) return `${o.title || ""}\n\n${o.content}\n\nMeta: ${o.meta_description || ""}`;
    return JSON.stringify(o, null, 2);
  }
  return String(o ?? "");
}

const ContentAutomation: React.FC = () => {
  const [tab, setTab] = useState<Tab>("generator");
  const [type, setType] = useState<ContentType>("social_post");
  const [product, setProduct] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [language, setLanguage] = useState("en");
  const [features, setFeatures] = useState("");
  const [offer, setOffer] = useState("");
  const [audience, setAudience] = useState("retail");
  const [keyword, setKeyword] = useState("");
  const [length, setLength] = useState(500);
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [copied, setCopied] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const res: any = await api.content.history();
      setHistory(res?.items || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load history");
    }
  }, []);

  const loadUsage = useCallback(async () => {
    try {
      const res: any = await api.content.usage();
      setUsage(res?.stats || null);
    } catch (e: any) {
      console.error("Failed to load usage:", e);
    }
  }, []);

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "usage") loadUsage();
  }, [tab, loadHistory, loadUsage]);

  const generate = async () => {
    setLoading(true);
    setError("");
    setOutput("");
    try {
      let res: any;
      if (type === "social_post") {
        if (!product.trim()) {
          throw new Error("Product name is required");
        }
        res = await api.content.socialPost({ product: product.trim(), platform, language });
        setOutput(res?.caption || "");
      } else if (type === "product_description") {
        if (!product.trim()) {
          throw new Error("Product name is required");
        }
        const feats = features.split("\n").map((f) => f.trim()).filter(Boolean);
        res = await api.content.productDesc({ product_name: product.trim(), features: feats });
        setOutput(res?.description || "");
      } else if (type === "whatsapp_message") {
        if (!offer.trim()) {
          throw new Error("Offer description is required");
        }
        res = await api.content.whatsappMsg({ offer: offer.trim(), audience });
        setOutput(res?.message || "");
      } else if (type === "blog_post") {
        if (!keyword.trim()) {
          throw new Error("Keyword is required");
        }
        res = await api.content.blogPost({ keyword: keyword.trim(), length });
        setOutput(
          `${res?.title || ""}\n\n${res?.content || ""}\n\nMeta: ${res?.meta_description || ""}\nTags: ${(res?.tags || []).join(", ")}`
        );
      }
    } catch (e: any) {
      setError(e?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyOutput = () => {
    if (!output) return;
    
    navigator.clipboard.writeText(output)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error("Failed to copy:", err);
        setError("Failed to copy to clipboard");
      });
  };

  const applyTemplate = (tpl: Template) => {
    setType(tpl.type);
    const p = tpl.preset;
    if (p.product !== undefined) setProduct(p.product);
    if (p.platform !== undefined) setPlatform(p.platform);
    if (p.language !== undefined) setLanguage(p.language);
    if (p.features !== undefined) setFeatures(p.features);
    if (p.offer !== undefined) setOffer(p.offer);
    if (p.audience !== undefined) setAudience(p.audience);
    if (p.keyword !== undefined) setKeyword(p.keyword);
    if (p.length !== undefined) setLength(p.length);
    setOutput("");
    setError("");
    setTab("generator");
  };

  const sendToWhatsApp = () => {
    if (!output) return;
    // Use business WhatsApp number
    const businessNumber = "+971581778894";
    const encodedText = encodeURIComponent(output);
    const url = `https://wa.me/${businessNumber}?text=${encodedText}`;
    window.open(url, "_blank");
  };

  const clearForm = () => {
    setProduct("");
    setPlatform("instagram");
    setLanguage("en");
    setFeatures("");
    setOffer("");
    setAudience("retail");
    setKeyword("");
    setLength(500);
    setOutput("");
    setError("");
  };

  return (
    <div style={{ padding: 32, minHeight: "100vh", background: "#060e20" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Manrope", color: "#dde5ff", margin: 0, fontSize: 28 }}>
            Content Automation
          </h1>
          <p style={{ color: "rgba(221,229,255,0.6)", margin: "4px 0 0", fontSize: 13 }}>
            AI-generated captions, product copy, broadcasts, and SEO posts.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(["generator", "templates", "history", "usage"] as Tab[]).map((t) => (
            <button
              key={t}
              style={tab === t ? buttonStyle : ghostButton}
              onClick={() => setTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ 
          ...glassCard, 
          marginBottom: 20, 
          borderLeft: "3px solid #ff6e84",
          color: "#ff6e84"
        }}>
          {error}
          <button 
            style={{ 
              position: "absolute", 
              right: 16, 
              top: 16, 
              background: "none", 
              border: "none", 
              color: "#ff6e84",
              cursor: "pointer",
              fontSize: 18
            }}
            onClick={() => setError("")}
          >
            ×
          </button>
        </div>
      )}

      {tab === "generator" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={{ ...glassCard, position: "relative" }}>
            <button
              style={{
                position: "absolute",
                right: 16,
                top: 16,
                background: "none",
                border: "none",
                color: "rgba(221,229,255,0.5)",
                cursor: "pointer",
                fontSize: 18
              }}
              onClick={clearForm}
              title="Clear form"
            >
              ↻
            </button>
            
            <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", marginBottom: 6 }}>
              Content Type
            </label>
            <select
              style={inputStyle}
              value={type}
              onChange={(e) => {
                setType(e.target.value as ContentType);
                setOutput("");
                setError("");
              }}
            >
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>

            {(type === "social_post" || type === "product_description") && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Product *
                </label>
                <input
                  style={inputStyle}
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                  placeholder="e.g. ENZO Italy Pro Hair Dryer X3"
                  maxLength={200}
                />
              </>
            )}

            {type === "social_post" && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Platform
                </label>
                <select style={inputStyle} value={platform} onChange={(e) => setPlatform(e.target.value)}>
                  <option value="instagram">Instagram (2200 chars)</option>
                  <option value="facebook">Facebook (500 chars)</option>
                  <option value="tiktok">TikTok (150 chars)</option>
                  <option value="linkedin">LinkedIn (500 chars)</option>
                </select>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Language
                </label>
                <select style={inputStyle} value={language} onChange={(e) => setLanguage(e.target.value)}>
                  <option value="en">English</option>
                  <option value="ar">Arabic (Gulf dialect)</option>
                </select>
              </>
            )}

            {type === "product_description" && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Features (one per line)
                </label>
                <textarea
                  style={{ ...inputStyle, minHeight: 120, fontFamily: "inherit" }}
                  value={features}
                  onChange={(e) => setFeatures(e.target.value)}
                  placeholder={"Ionic technology\n2400W motor\nCeramic barrel"}
                />
              </>
            )}

            {type === "whatsapp_message" && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Offer *
                </label>
                <input
                  style={inputStyle}
                  value={offer}
                  onChange={(e) => setOffer(e.target.value)}
                  placeholder="e.g. 20% off all LIZZE keratin this week"
                  maxLength={500}
                />
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Audience
                </label>
                <select style={inputStyle} value={audience} onChange={(e) => setAudience(e.target.value)}>
                  <option value="retail">Retail Customers</option>
                  <option value="wholesale">Wholesale Partners</option>
                  <option value="salon">Salons & Barbers</option>
                  <option value="distributor">Distributors</option>
                </select>
              </>
            )}

            {type === "blog_post" && (
              <>
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Keyword *
                </label>
                <input
                  style={inputStyle}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="e.g. best keratin treatment dubai"
                  maxLength={100}
                />
                <label style={{ display: "block", fontSize: 12, color: "rgba(221,229,255,0.6)", margin: "14px 0 6px" }}>
                  Length (words)
                </label>
                <input
                  type="number"
                  style={inputStyle}
                  value={length}
                  onChange={(e) => setLength(parseInt(e.target.value || "500", 10))}
                  min={200}
                  max={2000}
                />
              </>
            )}

            <button
              style={{ 
                ...buttonStyle, 
                marginTop: 20, 
                opacity: loading ? 0.6 : 1,
                width: "100%"
              }}
              onClick={generate}
              disabled={loading}
            >
              {loading ? "Generating…" : "Generate Content"}
            </button>
          </div>

          <div style={glassCard}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "rgba(221,229,255,0.6)" }}>Result</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button 
                  style={ghostButton} 
                  onClick={copyOutput} 
                  disabled={!output || copied}
                >
                  {copied ? "✓ Copied" : "Copy"}
                </button>
                <button 
                  style={ghostButton} 
                  onClick={sendToWhatsApp} 
                  disabled={!output}
                >
                  Send to WhatsApp
                </button>
              </div>
            </div>
            <pre
              style={{
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                background: "rgba(6,14,32,0.6)",
                borderRadius: 12,
                padding: 16,
                minHeight: 300,
                color: "#dde5ff",
                fontSize: 14,
                margin: 0,
                overflow: "auto",
                maxHeight: 500,
              }}
            >
              {output || "Generated content will appear here."}
            </pre>
          </div>
        </div>
      )}

      {tab === "templates" && (
        // M2 fix (audit): minmax(280px) overflows on narrow phones.
        <div className={grids["responsive-grid"]} style={{ gap: 16 }}>
          {TEMPLATES.map((tpl) => (
            <div key={tpl.id} style={glassCard}>
              <div style={{ color: "#cfeda2", fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                {TYPE_LABELS[tpl.type]}
              </div>
              <div style={{ color: "#dde5ff", fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
                {tpl.label}
              </div>
              <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 13, minHeight: 40, marginBottom: 14 }}>
                {tpl.description}
              </div>
              <button style={buttonStyle} onClick={() => applyTemplate(tpl)}>
                Use Template
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === "history" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 13 }}>
              Last {history.length} generations
            </div>
            <button style={ghostButton} onClick={loadHistory}>
              Refresh
            </button>
          </div>
          
          {history.length === 0 && (
            <div style={{ ...glassCard, textAlign: "center", color: "rgba(221,229,255,0.6)" }}>
              No content generated yet. Use the generator to create content.
            </div>
          )}
          {history.map((item) => (
            <div key={item.id} style={glassCard}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ color: "#cfeda2", fontSize: 13, fontWeight: 600 }}>
                  {TYPE_LABELS[item.type as ContentType] || item.type}
                </div>
                <div style={{ color: "rgba(221,229,255,0.5)", fontSize: 12 }}>
                  {new Date(item.created_at).toLocaleString()}
                </div>
              </div>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                  fontSize: 13,
                  color: "#dde5ff",
                  margin: 0,
                  maxHeight: 220,
                  overflow: "auto",
                }}
              >
                {renderOutput(item)}
              </pre>
            </div>
          ))}
        </div>
      )}

      {tab === "usage" && usage && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div style={glassCard}>
            <h3 style={{ color: "#dde5ff", marginTop: 0, marginBottom: 20 }}>Usage Statistics</h3>
            
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 12, marginBottom: 4 }}>Total Generations</div>
              <div style={{ color: "#cfeda2", fontSize: 32, fontWeight: 700 }}>
                {usage.total_generations}
              </div>
            </div>
            
            <div>
              <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 12, marginBottom: 8 }}>By Type</div>
              {Object.entries(usage.by_type).map(([type, count]) => (
                <div key={type} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ color: "#dde5ff", fontSize: 14 }}>
                    {TYPE_LABELS[type as ContentType] || type}
                  </div>
                  <div style={{ color: "#bdb2ff", fontWeight: 600 }}>
                    {count}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div style={glassCard}>
            <h3 style={{ color: "#dde5ff", marginTop: 0, marginBottom: 20 }}>Cost Estimate</h3>
            
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 12, marginBottom: 4 }}>Estimated API Cost</div>
              <div style={{ color: "#bdb2ff", fontSize: 32, fontWeight: 700 }}>
                ${usage.estimated_cost.toFixed(4)}
              </div>
              <div style={{ color: "rgba(221,229,255,0.5)", fontSize: 12, marginTop: 4 }}>
                Based on GPT-4o-mini pricing (~$0.0003/1K tokens)
              </div>
            </div>
            
            <div style={{ 
              background: "rgba(6,14,32,0.6)", 
              borderRadius: 12, 
              padding: 16,
              marginTop: 20
            }}>
              <div style={{ color: "rgba(221,229,255,0.6)", fontSize: 12, marginBottom: 8 }}>Notes</div>
              <ul style={{ 
                color: "#dde5ff", 
                fontSize: 13, 
                margin: 0, 
                paddingLeft: 16,
                lineHeight: 1.6
              }}>
                <li>Costs are estimates based on average token usage</li>
                <li>Actual OpenAI billing may vary</li>
                <li>Monitor your OpenAI dashboard for exact costs</li>
                <li>Consider setting usage limits in OpenAI account</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentAutomation;